import React, { useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Separator } from "@shared/ui/separator";
import { CreditCard, User, DollarSign, FileText } from "lucide-react";
import dayjs from "dayjs";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  PaymentDetail,
  CreateCreditNotePayload,
} from "@features/cx/payments/types";
import {
  useCreateCreditNote,
  useCreditNoteReasons,
  usePaymentDetail,
} from "@features/cx/payments/hooks";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { normalizeDate } from "@shared/utils/dateUtils";

interface CreditNoteDialogProps {
  open: boolean;
  onClose: () => void;
  payment?: PaymentDetail;
  excessAmount?: number;
  remoteUtilityId: number;
}

export function CreditNoteDialog({
  open,
  onClose,
  payment,
  excessAmount = 0,
  remoteUtilityId,
}: CreditNoteDialogProps) {
  console.log("payment", payment);
  if (!payment) return null;
  const { data: paymentData } = usePaymentDetail({
    paymentId: payment.id,
    remoteUtilityId: payment.remoteUtilityId,
  });
  console.log("paymentData", paymentData);
  const createCreditNoteMutation = useCreateCreditNote(remoteUtilityId);
  const { data: creditNoteReasons } = useCreditNoteReasons(remoteUtilityId);
  const currency = useCurrency();
  // Calculate maximum credit amount and default value
  const maxCreditAmount = Number(payment?.amount) || excessAmount;
  const defaultCreditAmount = Number(payment?.amount) || excessAmount;

  // Define form fields using FormService pattern
  const creditNoteFields: FormField[] = [
    {
      name: "creditAmount",
      label: "Credit Amount",
      type: "decimal",
      required: true,
      placeholder: "0.00",
      defaultValue: defaultCreditAmount,
      min: 0.01,
      max: maxCreditAmount,
      step: 0.01,
      prefix: "$",
      helperText: `Maximum: ${currency} ${maxCreditAmount.toFixed(2)}`,
      showHelperTooltip: true,
      tooltipText:
        "Enter the amount to be credited. Cannot exceed the original payment amount.",
      fullWidth: true,
      customValidation: (value: any) => {
        const numValue = Number(value);
        if (isNaN(numValue) || numValue <= 0) {
          return "Credit amount must be greater than 0";
        }
        if (numValue > maxCreditAmount) {
          return `Credit amount cannot exceed $${maxCreditAmount.toFixed(2)}`;
        }
        return true;
      },
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-12 px-4 pl-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "reason",
      label: "Reason for Credit Note",
      type: "select",
      required: true,
      placeholder: "Select a reason",
      helperText: "Select the reason for issuing this credit note",
      showHelperTooltip: true,
      tooltipText:
        "Choose the appropriate reason for the credit note. This will be recorded for audit purposes.",
      fullWidth: true,
      options:
        creditNoteReasons?.result?.map((reason) => ({
          value: reason.key,
          label: reason.value,
        })) || [],
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "remarks",
      label: "Remarks (Optional)",
      type: "textarea",
      required: false,
      placeholder: "Enter additional remarks or notes about this credit note",
      maxLength: 500,
      rows: 4,
      helperText: "Optional internal notes (max 500 characters)",
      showHelperTooltip: true,
      tooltipText:
        "Add any additional notes or comments about this credit note for internal reference.",
      fullWidth: true,
      classes: {
        container: "w-full mb-6 col-span-2",
        label: "block text-sm font-medium text-gray-700 mb-2",
        textarea:
          "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
  ];

  // Create Form Instance using FormService
  const creditNoteForm = FormService.createForm({
    fields: creditNoteFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      creditAmount: defaultCreditAmount,
      reason: "",
      remarks: "",
    },
  });

  // Reset form when dialog opens with new payment data
  useEffect(() => {
    if (open && payment) {
      creditNoteForm.reset({
        creditAmount: defaultCreditAmount,
        reason: "",
        remarks: "",
      });
    }
  }, [open, payment, defaultCreditAmount, creditNoteForm]);

  // Handle form submission
  const handleCreateCreditNote = useCallback(async () => {
    if (!payment) {
      console.error("No payment data available");
      return;
    }

    // Convert and validate credit amount
    const creditAmount = Number(creditNoteForm.getValues("creditAmount"));

    if (isNaN(creditAmount) || creditAmount <= 0) {
      creditNoteForm.setError("creditAmount", {
        type: "manual",
        message: "Credit amount must be greater than 0",
      });
      return;
    }

    if (creditAmount > maxCreditAmount) {
      creditNoteForm.setError("creditAmount", {
        type: "manual",
        message: `Credit amount cannot exceed ${currency} ${maxCreditAmount.toFixed(2)}`,
      });
      return;
    }

    // Resolve actual data (handling potential missing .result wrapper)
    const actualData = (paymentData?.result || paymentData) as PaymentDetail;

    // Create credit note payload according to the API structure
    const basePayload = {
      remoteUtilityId,
      amount: creditAmount,
      paymentDate: normalizeDate(payment.paymentDate),
      consumer_id:
        actualData?.consumer?.id ||
        payment?.consumer?.id ||
        payment?.consumerId,
      creditNoteReason: Number(creditNoteForm.getValues("reason")),
      extraData: {
        referencePaymentId: payment.id,
        remark: creditNoteForm.getValues("remarks"),
      },
    };

    console.log("Base Payload", basePayload);
    // Add conditional fields based on payment type
    let creditNotePayload: any;

    if (payment.paymentPayType === "Service Payment") {
      const supportRequest = actualData?.consumerSupportRequest;
      creditNotePayload = {
        ...basePayload,
        consumerSupportRequest: supportRequest
          ? Number(supportRequest)
          : undefined,
      };
    } else if (payment.paymentPayType === "Bill Payment") {
      const billId = actualData?.remoteBillId;
      creditNotePayload = {
        ...basePayload,
        remoteBillId: billId ? Number(billId) : undefined,
      };
    } else {
      creditNotePayload = {
        ...basePayload,
      };
    }

    // Use the mutation hook to create the credit note
    createCreditNoteMutation.mutate(creditNotePayload, {
      onSuccess: () => {
        // Close dialog on success
        onClose();
      },
      onError: (error: any) => {
        // Handle specific errors if needed
        console.error("Error creating credit note:", error);
      },
    });
  }, [
    payment,
    maxCreditAmount,
    creditNoteForm,
    createCreditNoteMutation,
    remoteUtilityId,
    onClose,
    paymentData,
  ]);

  // Handle form field changes (optional - for any custom logic)
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Add any custom logic here if needed
    console.log(`Field ${fieldName} changed to:`, value);
  }, []);

  // Handle dialog close
  const handleClose = useCallback(() => {
    if (!createCreditNoteMutation.isPending) {
      creditNoteForm.reset();
      onClose();
    }
  }, [createCreditNoteMutation.isPending, creditNoteForm, onClose]);

  // Don't render if no payment data
  if (!payment) return null;

  // Watch current form values for summary calculations
  const currentCreditAmount = Number(creditNoteForm.watch("creditAmount")) || 0;
  const remainingAmount = Number(payment.amount) - currentCreditAmount;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Create Credit Note
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 pr-2">
          {/* Payment Details */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Payment Details</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Consumer</p>
                  <p className="font-medium">
                    {payment.consumerFirstName + " " + payment.consumerLastName}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Account Number</p>
                  <p className="font-medium">{payment.consumerNo}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Receipt Number</p>
                  <p className="font-medium">{payment.receiptNo}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Payment Amount</p>
                  <p className="font-medium">
                    {currency} {Number(payment.amount).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Payment Date</p>
                  <p className="font-medium">{payment.paymentDate}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Payment Method</p>
                  <p className="font-medium">
                    {paymentData?.result?.paymentMode}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Separator />

          {/* Credit Note Form */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Credit Note Details</span>
            </div>

            <DynamicForm
              fields={creditNoteFields}
              form={creditNoteForm}
              onSubmit={handleCreateCreditNote}
              onFieldChange={handleFieldChange}
              loading={createCreditNoteMutation.isPending}
              config={{
                showSubmitButton: false,
                showResetButton: false,
              }}
              gridColumns={2}
            />
          </div>

          {/* Summary */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Original Payment Amount:</span>
                  <span className="font-medium">
                    {currency} {Number(payment.amount).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Credit Note Amount:</span>
                  <span className="font-bold text-green-600">
                    {currency} {currentCreditAmount.toFixed(2)}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span>Remaining Payment Value:</span>
                  <span className="font-medium">
                    {currency} {remainingAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="flex-shrink-0 mt-4 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={createCreditNoteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={creditNoteForm.handleSubmit(handleCreateCreditNote)}
            disabled={
              createCreditNoteMutation.isPending ||
              !creditNoteForm.formState.isValid ||
              currentCreditAmount <= 0 ||
              currentCreditAmount > maxCreditAmount
            }
            className="min-w-[140px]"
          >
            {createCreditNoteMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </>
            ) : (
              "Create Credit Note"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
